import Layout from "@components/common/Layout";
import SEO from "@components/common/Layout/SEO";
import OverViewWrapper from "@components/common/OverviewWrapper";
import WebinarHero from "@components/webinar/Hero";
import useDynamicZone from "@hooks/use-dyamic-zone";
import useOverViewMenu from "@hooks/use-overview-menu";
import { HeroBannerPropsModel } from "@models/hero-banner";
import { NavigationResponseModel } from "@models/layout";
import { PageContextModel } from "@models/page";
import { ProductSearchDynamicZones } from "@models/product-search";
import { SeoDataModel } from "@models/seo";
import { useDebounce } from "@uidotdev/usehooks";
import { AnimatePresence, Variants, motion } from "framer-motion";
import { HeadFC, PageProps, graphql } from "gatsby";
import React, { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface PageDataModel {
  strapiOverviewPage: {
    title: string;
    seo: SeoDataModel;
    topBanner: HeroBannerPropsModel;
    dynamicZoneComponents: ProductSearchDynamicZones[];
    darkTheme: boolean;
  };
  navigation: NavigationResponseModel;
}

const overviewMenuVariants: Variants = {
  initial: {
    opacity: 0,
  },
  enter: {
    opacity: 1,
  },
  leave: {
    opacity: 0,
  },
};

const OverViewPage = (props: PageProps<PageDataModel, PageContextModel>) => {
  const { data, path } = props ?? {};
  const { strapiOverviewPage, navigation } = data ?? {};
  const { topBanner, dynamicZoneComponents } = strapiOverviewPage ?? {};
  const { renderDynamicZone } = useDynamicZone({});
  const ref = useRef<HTMLDivElement | null>(null);
  const { isInView } = useOverViewMenu(ref);
  const [lastestId, setlatestId] = useState("");

  const handleInViewOutView = (id: string, type: "inview" | "outview") => {
    if (type === "inview" && id) {
      setlatestId(id);
    }
  };

  const debouncedLatedId = useDebounce(lastestId, 500);

  const menuTexts: string[] = dynamicZoneComponents
    ?.filter((dynamicZone) => {
      return !!dynamicZone?.menuText;
    })
    .map((d) => d?.menuText) as string[];

  const renderOverViewMenu = () => {
    return (
      <motion.div
        className="sticky top-[0px] gap-4 hidden md:flex px-8 xl:px-40 bg-gray-50 w-full py-4 z-20 justify-center flex-wrap"
        variants={overviewMenuVariants}
        initial="initial"
        animate="enter"
        exit="leave"
      >
        {dynamicZoneComponents
          .filter((dynamicZone) => {
            return !!dynamicZone?.menuText;
          })
          .map((dynamicZone) => {
            const { id, menuText } = dynamicZone;
            return (
              <div
                key={id}
                className={twMerge(
                  "border rounded-3xl hover:bg-dark-purple-30 hover:text-white border-dark-purple-50 text-dark-purple-50",
                  debouncedLatedId === id ? "bg-dark-purple-50 text-white" : ""
                )}
              >
                <a href={`#${menuText}`} className="block px-2 py-1 text-sm">
                  {menuText}
                </a>
              </div>
            );
          })}
      </motion.div>
    );
  };

  return (
    <Layout
      style={{
        height: "100dvh",
        overflowY: "auto",
        scrollBehavior: "smooth",
      }}
    >
      <WebinarHero
        {...topBanner}
        ctaLink={topBanner?.ctaBtnLink}
        ctaText={topBanner?.ctaBtnText}
        secondaryNav={navigation}
        path={path}
      />
      <div ref={ref} className="relative" style={{}}>
        <AnimatePresence>
          {isInView ? (
            renderOverViewMenu()
          ) : (
            <div className="h-[76px]">&nbsp;</div>
          )}
        </AnimatePresence>
        {dynamicZoneComponents?.map((dynamicZone, index) => {
          const { id } = dynamicZone;
          return (
            <div key={id} className="relative">
              {index === 0 && (
                <div
                  id={menuTexts[0]}
                  className="h-24 bg-transparent absolute top-0 w-full"
                >
                  &nbsp;
                </div>
              )}
              <OverViewWrapper id={id} callback={handleInViewOutView}>
                {renderDynamicZone(dynamicZone, "")}

                {
                  <div
                    id={menuTexts[index + 1]}
                    className="h-24 bg-transparent absolute bottom-0 w-full"
                  >
                    &nbsp;
                  </div>
                }
              </OverViewWrapper>
            </div>
          );
        })}
      </div>
    </Layout>
  );
};
export default OverViewPage;

export const overviewPage = graphql`
  query overviewPageQuery {
    navigation(slug: { eq: "search" }) {
      id
      items {
        data {
          id
          attributes {
            order
            title
            url
            target
            # border_left
            border_bottom
            type_of_menu
            updatedAt
            children {
              data {
                id
                attributes {
                  # border_bottom
                  # border_left
                  target
                  title
                  type_of_menu
                  url
                }
              }
            }
          }
        }
      }
    }
    strapiOverviewPage(slug: { eq: "search-overview" }) {
      id
      title
      seo {
        ...seoFragment
      }
      topBanner {
        bigHeader
        ctaBtnLink
        ctaBtnText
        description
        title
      }
      dynamicZoneComponents {
        ... on STRAPI__COMPONENT_COMMON_LAST_SCROLL_CTA {
          ...lastScrollCtaFragment
        }
        ... on STRAPI__COMPONENT_COMMON_FEATURE_STATS {
          ...featureStatsFragment
          subTitle
          menuText
        }
        ... on STRAPI__COMPONENT_COMMON_THREE_POINT_HIGHLIGHTER {
          id
          bgColor
          imgPostion
          strapi_component
          subTitle
          title
          menuText
          tiles {
            id
            icon
            subTitle
            title
            color
          }
          threePointImage: image {
            localFile {
              childImageSharp {
                gatsbyImageData(placeholder: NONE, quality: 100, width: 658)
              }
            }
          }
        }
        ... on STRAPI__COMPONENT_COMMON_TILE_WRAPPER {
          id
          menuText
          strapi_component
          title
          subTitle
          learnMoreLink
          tiles {
            id
            title
            subTitle
            icon
            color
          }
          variant
          bgColor
          menuText
          # tileWrapperClassName
          # titleClassName
          tileWrapperImage: image {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 584, height: 476, placeholder: NONE)
              }
            }
          }
        }
        ... on STRAPI__COMPONENT_COMMON_IMAGE_WRAPPER {
          id
          menuText
          strapi_component
          title
          subTitle
          variant
          imgPosition
          menuText
          divColor
          imageWrapper: image {
            localFile {
              childImageSharp {
                gatsbyImageData(
                  formats: WEBP
                  quality: 100
                  height: 483
                  # width: 800
                )
              }
            }
          }
          learnMoreLink
        }
        ... on STRAPI__COMPONENT_COMMON_TIMED_FEATURE_LIST {
          id
          title
          subTitle
          learnMoreLink
          learnMoreText
          strapi_component
          color
          bgColor
          progressEmptyColor
          progressFillColor
          menuText
          timedFeatures {
            id
            order
            featureIcon
            featureTitle
            featureSubTitle
            featureImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(placeholder: NONE, formats: WEBP)
                }
              }
            }
          }
        }
        ... on STRAPI__COMPONENT_CATEGORY_IMAGE_TEXT_BLOCK {
          id
          imgRight
          learnMoreLink
          strapi_component
          subTitle
          textColor
          title
          bgColor
          menuText
          textBlockImage: image {
            localFile {
              childImageSharp {
                gatsbyImageData(
                  height: 328
                  width: 508
                  placeholder: BLURRED
                  formats: WEBP
                )
              }
            }
          }
        }
      }
    }
  }
`;

export const Head: HeadFC<PageDataModel, PageContextModel> = ({
  data,
  location,
}) => {
  const { title, seo } = data?.strapiOverviewPage ?? {};
  const pathname = location?.pathname;
  return <SEO title={title} pathname={pathname} {...(seo ?? {})} />;
};
